import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import type {
    GetProductsByCategoryQuery,
    GetProductsByCategoryQueryVariables,
} from 'generated/graphql';
import { GetProductsByCategoryDocument } from 'generated/graphql';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { mapProductToTileViewModel } from '~/modules-rocco/products/data-mappers/tile-product';

export const RELATED_CATEGORY_PRODUCTS_API_ROUTE =
    '/remix-api/app/product/related-category-products';

const MAX_VISIBLE_PRODUCTS = 10;
const FETCH_LIMIT = 12;

export interface RelatedCategoryProductsApiResponse {
    categoryProducts: {
        products: ProductTileViewModel[];
        pageInfo: {
            totalCount: number;
        };
    };
}

const QUERY_SCHEMA = z.object({
    source: z.string().min(1),
    categoryID: z.coerce.number().int().positive(),
    currentProductID: z.coerce.number().int().positive(),
    appDisplayMode: z.enum(['mobile', 'desktop']).default('desktop'),
});

export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        source: url.searchParams.get('source'),
        categoryID: url.searchParams.get('categoryID'),
        currentProductID: url.searchParams.get('currentProductID'),
        appDisplayMode: url.searchParams.get('appDisplayMode') ?? 'desktop',
    });

    if (!result.success) {
        return routerData<RelatedCategoryProductsApiResponse>(
            {
                categoryProducts: {
                    products: [],
                    pageInfo: { totalCount: 0 },
                },
            },
            { status: 400 },
        );
    }

    const { source, categoryID, currentProductID, appDisplayMode } =
        result.data;

    try {
        const queryResult = await context.client.query<
            GetProductsByCategoryQuery,
            GetProductsByCategoryQueryVariables
        >({
            query: GetProductsByCategoryDocument,
            variables: {
                source,
                categoryID,
                limit: FETCH_LIMIT,
            },
        });

        const nodes =
            queryResult.data?.listDirectoryItemsByCategory?.nodes ?? [];

        // Filter out current product and non-product nodes
        const filteredProducts = nodes.filter(
            (node): node is Extract<typeof node, { __typename: 'Product' }> =>
                node.__typename === 'Product' && node.ID !== currentProductID,
        );

        const hasMore = filteredProducts.length > MAX_VISIBLE_PRODUCTS;
        const products = filteredProducts
            .slice(0, MAX_VISIBLE_PRODUCTS)
            .map((node) =>
                mapProductToTileViewModel(node, context.user, {
                    appDisplayMode,
                }),
            );

        return {
            categoryProducts: {
                products,
                pageInfo: {
                    totalCount: hasMore ? products.length + 1 : products.length,
                },
            },
        };
    } catch (error) {
        context.logger.error(
            `Error in ${RELATED_CATEGORY_PRODUCTS_API_ROUTE}`,
            {
                error,
                categoryID,
                currentProductID,
                source,
            },
        );

        return routerData<RelatedCategoryProductsApiResponse>(
            {
                categoryProducts: {
                    products: [],
                    pageInfo: { totalCount: 0 },
                },
            },
            { status: 500 },
        );
    }
};
